/**
 * Agent Activity Stream - Real-time stream of agent status updates
 * Provides live agent status updates with <500ms latency via WebSocket
 * Implements UX-SALLY-002: Real-Time Agent Activity Stream
 * Implements Epic 3 real-time visualization patterns
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useOrchestrationStore } from '../../stores/orchestrationStore';

export interface AgentActivityEvent {
  id: string;
  agentId: string;
  agentName: string;
  type: 'status_change' | 'task_start' | 'task_complete' | 'error' | 'progress_update' | 'handoff';
  timestamp: number;
  data: Record<string, any>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  progress?: number;
}

interface AgentActivityStreamProps {
  className?: string;
  maxEvents?: number;
  showFilters?: boolean;
  realTimeEnabled?: boolean;
  campaignId?: string;
  onAgentHandoff?: (fromAgent: string, toAgent: string) => void;
}

export const AgentActivityStream: React.FC<AgentActivityStreamProps> = ({
  className = '',
  maxEvents = 50,
  showFilters = true,
  realTimeEnabled = true,
  campaignId,
  onAgentHandoff
}) => {
  const { agents, isConnected, performanceMetrics } = useOrchestrationStore();
  const [activityStream, setActivityStream] = useState<AgentActivityEvent[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttemptsRef = useRef(5);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!realTimeEnabled || !campaignId) return;

    const connectWebSocket = () => {
      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${protocol}//${window.location.host}/api/agent-activity/ws`;
        
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          setWsConnected(true);
          reconnectAttemptsRef.current = 0;
          
          // Subscribe to campaign activity
          ws.send(JSON.stringify({
            type: 'subscribe',
            campaignId
          }));
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            
            if (message.type === 'agent_event') {
              const newEvent: AgentActivityEvent = {
                id: message.event_id || `event_${Date.now()}`,
                agentId: message.agent_id,
                agentName: message.agent_name,
                type: message.event_type,
                timestamp: message.timestamp || Date.now(),
                data: message.context_data || {},
                severity: message.severity || 'low',
                progress: message.progress_percent
              };

              setActivityStream(prev => {
                const combined = [newEvent, ...prev];
                return combined.slice(0, maxEvents);
              });
              setLastUpdate(Date.now());

              // Handle agent handoff
              if (message.event_type === 'handoff' && onAgentHandoff) {
                onAgentHandoff(message.from_agent, message.to_agent);
              }
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          setWsConnected(false);
        };

        ws.onclose = () => {
          setWsConnected(false);
          
          // Attempt reconnection
          if (reconnectAttemptsRef.current < maxReconnectAttemptsRef.current) {
            reconnectAttemptsRef.current++;
            setTimeout(connectWebSocket, 1000 * reconnectAttemptsRef.current);
          }
        };

        wsRef.current = ws;
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [realTimeEnabled, campaignId, maxEvents, onAgentHandoff]);

  // Generate activity events based on agent state changes
  useEffect(() => {
    if (!realTimeEnabled) return;

    const generateActivityEvent = (agentId: string, agent: { name?: string; type?: string; status?: string }): AgentActivityEvent | null => {
      const now = Date.now();
      
      // Don't generate events for agents that haven't changed recently
      if (agent.lastUpdated && now - agent.lastUpdated > 1000) return null;

      const eventTypes: Record<string, Partial<AgentActivityEvent>> = {
        executing: {
          type: 'status_change',
          severity: 'medium',
          data: { status: 'executing', task: agent.currentTask }
        },
        error: {
          type: 'error',
          severity: 'critical',
          data: { error: agent.error || 'Unknown error', status: 'error' }
        },
        completed: {
          type: 'task_complete',
          severity: 'low',
          data: { status: 'completed', completedTask: agent.currentTask }
        },
        idle: {
          type: 'status_change',
          severity: 'low',
          data: { status: 'idle' }
        }
      };

      const eventTemplate = eventTypes[agent.status];
      if (!eventTemplate) return null;

      return {
        id: `${agentId}_${now}_${Math.random().toString(36).substr(2, 9)}`,
        agentId,
        agentName: agent.name || `Agent ${agentId}`,
        timestamp: now,
        ...eventTemplate
      } as AgentActivityEvent;
    };

    // Monitor agent changes and generate events
    const updateInterval = setInterval(() => {
      const newEvents: AgentActivityEvent[] = [];
      
      Object.entries(agents).forEach(([agentId, agent]) => {
        const event = generateActivityEvent(agentId, agent);
        if (event) {
          newEvents.push(event);
        }
      });

      if (newEvents.length > 0) {
        setActivityStream(prev => {
          const combined = [...newEvents, ...prev];
          return combined.slice(0, maxEvents);
        });
        setLastUpdate(Date.now());
      }
    }, 200); // 200ms update interval for <500ms latency requirement

    return () => clearInterval(updateInterval);
  }, [agents, realTimeEnabled, maxEvents]);

  // Filtered events based on user selection
  const filteredEvents = useMemo(() => {
    return activityStream.filter(event => {
      const typeMatch = filterType === 'all' || event.type === filterType;
      const severityMatch = filterSeverity === 'all' || event.severity === filterSeverity;
      return typeMatch && severityMatch;
    });
  }, [activityStream, filterType, filterSeverity]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const getEventIcon = (type: string, severity: string) => {
    const icons = {
      status_change: '🔄',
      task_start: '▶️',
      task_complete: '✅',
      error: '❌',
      progress_update: '📊',
      handoff: '🤝'
    };
    
    if (severity === 'critical') return '🚨';
    return icons[type as keyof typeof icons] || '•';
  };

  const getSeverityColor = (severity: string) => {
    const colors = {
      low: 'text-slate-400',
      medium: 'text-blue-400',
      high: 'text-amber-400',
      critical: 'text-red-400'
    };
    return colors[severity as keyof typeof colors] || 'text-slate-400';
  };

  const getEventDescription = (event: AgentActivityEvent) => {
    const { type, agentName, data } = event;
    
    switch (type) {
      case 'status_change':
        return `${agentName} status changed to ${data.status}`;
      case 'task_start':
        return `${agentName} started task: ${data.task || 'Unknown task'}`;
      case 'task_complete':
        return `${agentName} completed task: ${data.completedTask || 'Task'}`;
      case 'error':
        return `${agentName} encountered error: ${data.error}`;
      case 'progress_update':
        return `${agentName} is working: ${data.message || data.task || 'Processing'}`;
      case 'handoff':
        return `${agentName} passing to ${data.to_agent} - ${data.message || 'Handing off context'}`;
      default:
        return `${agentName} activity update`;
    }
  };

  return (
    <div className={cn('flex flex-col h-full bg-slate-800 text-slate-100', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-100">Activity Stream</h3>
          <div className={`w-2 h-2 rounded-full ${wsConnected || isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
          <span className="text-xs text-slate-400">
            {wsConnected ? '🔴 Live' : '⚪ Polling'}
          </span>
          {performanceMetrics.averageLatency && (
            <span className={`text-xs px-2 py-1 rounded ${
              performanceMetrics.averageLatency > 500 ? 'bg-red-600' : 'bg-emerald-600'
            }`}>
              {Math.round(performanceMetrics.averageLatency)}ms
            </span>
          )}
        </div>
        <div className="text-xs text-slate-400">
          {filteredEvents.length} events
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="p-3 border-b border-slate-700 flex gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-xs bg-slate-700 border border-slate-600 rounded px-2 py-1"
          >
            <option value="all">All Types</option>
            <option value="status_change">Status Changes</option>
            <option value="task_start">Task Starts</option>
            <option value="task_complete">Completions</option>
            <option value="error">Errors</option>
            <option value="progress_update">Progress</option>
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="text-xs bg-slate-700 border border-slate-600 rounded px-2 py-1"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      )}

      {/* Activity Stream */}
      <div className="flex-1 overflow-y-auto">
        {filteredEvents.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500">
            <div className="text-center">
              <div className="text-2xl mb-2">📡</div>
              <div className="text-sm">No recent activity</div>
              {!isConnected && (
                <div className="text-xs text-red-400 mt-1">Connection lost</div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-start gap-3 p-2 rounded hover:bg-slate-700/50 transition-colors border-l-2 border-slate-600"
              >
                <div className="flex-shrink-0 text-lg pt-1">
                  {getEventIcon(event.type, event.severity)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={cn(
                    'text-sm font-medium',
                    getSeverityColor(event.severity)
                  )}>
                    {getEventDescription(event)}
                  </div>
                  
                  {/* Progress bar for progress_update and task_start events */}
                  {(event.type === 'progress_update' || event.type === 'task_start') && event.progress !== undefined && (
                    <div className="mt-2 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-300"
                        style={{ width: `${Math.min(event.progress, 100)}%` }}
                      />
                    </div>
                  )}
                  
                  <div className="text-xs text-slate-500 mt-1">
                    {formatTimestamp(event.timestamp)}
                    {event.progress !== undefined && event.type === 'progress_update' && (
                      <span className="ml-2 text-slate-400">{event.progress}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-2 border-t border-slate-700 text-xs text-slate-500 text-center">
        Last update: {formatTimestamp(lastUpdate)} • Real-time: {realTimeEnabled ? 'ON' : 'OFF'}
      </div>
    </div>
  );
};

export default AgentActivityStream;
